import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Stack, Typography, Paper, TextField, Checkbox, FormControlLabel,
  Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Box, InputAdornment, CircularProgress,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import { apiGet, apiPatch, ApiError } from "../api/client";
import { useToast } from "../ui/Toaster";

export default function ManualUpdates() {
  const { error: toastError, success: toastSuccess } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [savingKeys, setSavingKeys] = useState(() => new Set());
  const [showResolved, setShowResolved] = useState(false);

  const fetchRows = useCallback(async (signal) => {
    setLoading(true);
    try {
      const js = await apiGet("/api/manual_updates", { signal });
      const data = Array.isArray(js?.results) ? js.results : Array.isArray(js) ? js : [];
      setRows(
        data.map((r, i) => ({
          id: r.id ?? r.rowid ?? `mu_${i}`,
          lot_id: r.lot_id ?? "",
          order_id: r.order_id ?? "",
          type: r.type ?? "",
          notes: r.notes ?? "",
          resolved: Boolean(r.resolved ?? false),
        }))
      );
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load manual updates", e instanceof ApiError ? e.message : String(e));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchRows(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchRows]);

  const setSaving = (key, on) => {
    setSavingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  const updateResolved = useCallback(async (lotId, nextResolved) => {
    const key = String(lotId || "");
    if (!key) return;
    setRows((cur) => cur.map((r) => (r.lot_id === lotId ? { ...r, resolved: nextResolved } : r)));
    setSaving(key, true);
    try {
      await apiPatch(`/api/manual_updates/${encodeURIComponent(key)}`, {
        lot_id: key, resolved: Boolean(nextResolved),
      });
      toastSuccess(nextResolved ? "Marked resolved" : "Reopened", lotId);
    } catch (e) {
      setRows((cur) => cur.map((r) => (r.lot_id === lotId ? { ...r, resolved: !nextResolved } : r)));
      toastError("Update failed", e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(key, false);
    }
  }, [toastError, toastSuccess]);

  const visibleRows = useMemo(() => {
    const s = q.trim().toLowerCase();
    let base = rows;
    if (!showResolved) base = base.filter((r) => !r.resolved);
    if (!s) return base;
    return base.filter((r) => (
      String(r.lot_id || "").toLowerCase().includes(s) ||
      String(r.order_id || "").toLowerCase().includes(s) ||
      String(r.type || "").toLowerCase().includes(s) ||
      String(r.notes || "").toLowerCase().includes(s)
    ));
  }, [rows, q, showResolved]);

  return (
    <Box>
      {/* Page header — matches OC's standard layout (h1 serif title + secondary subtitle + right-aligned actions) */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Manual Updates
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {visibleRows.length.toLocaleString()} item{visibleRows.length === 1 ? "" : "s"} need attention
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => fetchRows()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Filters card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search lot, order, notes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
                size="small"
              />
            }
            label="Show resolved"
          />
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 110 }}>Resolved</TableCell>
                <TableCell sx={{ width: 160 }}>Type</TableCell>
                <TableCell sx={{ width: 140 }}>Lot</TableCell>
                <TableCell sx={{ width: 160 }}>Order</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                      <BuildOutlinedIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Nothing pending</Typography>
                      <Typography variant="caption">
                        Manual fix-ups appear here.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.map((row) => {
                const key = String(row.lot_id || "");
                const saving = savingKeys.has(key);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ width: 110 }}>
                      <Checkbox
                        checked={!!row.resolved}
                        disabled={saving}
                        onChange={(e) => updateResolved(row.lot_id, e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ width: 160 }}>
                      {row.type ? (
                        <Chip size="small" label={row.type} color="warning" variant="outlined" />
                      ) : (
                        <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 140, fontFamily: 'mono', fontSize: '0.78rem' }}>
                      {row.lot_id || "—"}
                    </TableCell>
                    <TableCell sx={{ width: 160, fontFamily: 'mono', fontSize: '0.78rem' }}>
                      {row.order_id || "—"}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem', whiteSpace: 'normal' }}>
                      {row.notes || "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && visibleRows.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 8, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
            borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
            color: 'text.secondary',
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
      </Paper>
    </Box>
  );
}
